// Compose — admin-side DataLab article composer (PROP-52, epic PROP-41).
//
// Mirrors the agent composer (frontend/.../ArticleComposer.tsx) field for
// field, but every write is authored by the "ProProp Data Lab" house
// identity via the /admin/articles* endpoints. Submitting routes the
// article through the same ReviewQueue as agent articles — no publish
// bypass. Publish-vs-schedule is the reviewer's decision at approve time
// (the existing ApproveInput flow), so this surface has Save + Submit
// only; it does not carry a publish-timing control.
//
// Two routes share this page:
//   /editorial/compose       → ComposeStart  (pick a kind / resume a draft)
//   /editorial/compose/:id   → ComposeEditor (the composer proper)

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Icon,
  KindChip,
  PageHeader,
  ReviewPill,
  ghostBtn,
  pgKicker,
  primaryBtn,
  smallBtn,
  type KindKey,
} from "@/components/admin-ui";
import { useAdminArticle, useComposeActions, useHouseDrafts } from "@/lib/articles-hooks";
import { useTags } from "@/lib/tags-hooks";
import {
  articlesApiEnabled,
  uploadAdminArticleImage,
  type PatchInput,
  type Section,
} from "@/lib/articlesApi";

const KIND_KEYS: KindKey[] = ["URA", "Policy", "Market", "Brief", "Guide"];

const AUDIENCE_OPTS: { value: "public" | "members" | "pro"; label: string; sub: string }[] = [
  { value: "public", label: "Public", sub: "Live on the buyer Newsroom and Explore feeds." },
  { value: "members", label: "Signed-in members", sub: "Visible only to logged-in users." },
  { value: "pro", label: "Pros only", sub: "Internal — agent-facing surfaces." },
];

// ─── route dispatcher ────────────────────────────────────────────────────────

export function ComposePage() {
  const { id } = useParams<{ id?: string }>();
  return id ? <ComposeEditor key={id} id={id} /> : <ComposeStart />;
}

// ─── start screen — pick a kind or resume a draft ────────────────────────────

function ComposeStart() {
  const navigate = useNavigate();
  const actions = useComposeActions();
  const { drafts, loading: draftsLoading } = useHouseDrafts();
  const [kind, setKind] = useState<KindKey>("Market");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onCreate() {
    setCreating(true);
    setError(null);
    try {
      const created = await actions.create(kind);
      if (created) navigate(`/editorial/compose/${encodeURIComponent(created.id)}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setCreating(false);
    }
  }

  return (
    <>
      <PageHeader
        kicker="Editorial · DataLab"
        title="Compose"
        sub="Publish data-driven analysis under the ProProp Data Lab byline. Drafts route through the same review queue as agent articles."
      />
      <div style={{ padding: "0 26px 32px", display: "flex", flexDirection: "column", gap: 18 }}>
        {!articlesApiEnabled() && (
          <Notice tone="warn">
            The articles API is disabled (<code>VITE_ARTICLES_API</code> unset). Compose needs a
            live backend — creating and editing drafts will not work in offline-mock dev.
          </Notice>
        )}
        {error && <Notice tone="neg">Could not create the draft: {error}</Notice>}

        {/* New draft */}
        <section style={panel}>
          <div style={panelTitle}>New article</div>
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-3)", marginBottom: 12 }}>
            Pick a kind to start a draft. You can change it later in the composer.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
            {KIND_KEYS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                style={{
                  padding: "5px 12px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontFamily: "var(--font-ui)",
                  fontSize: 11.5,
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  background: kind === k ? "var(--accent-soft)" : "var(--surface)",
                  border: "1px solid",
                  borderColor: kind === k ? "var(--accent)" : "var(--rule)",
                  color: kind === k ? "var(--accent)" : "var(--ink-3)",
                }}
              >
                {k}
              </button>
            ))}
          </div>
          <button
            type="button"
            style={{ ...primaryBtn, opacity: creating || !actions.enabled ? 0.6 : 1 }}
            disabled={creating || !actions.enabled}
            onClick={() => void onCreate()}
          >
            {Icon.plus} {creating ? "Creating…" : "Create draft"}
          </button>
        </section>

        {/* Resume a draft */}
        <section style={panel}>
          <div style={panelTitle}>In-progress drafts</div>
          {draftsLoading ? (
            <div style={emptyText}>Loading…</div>
          ) : drafts.length === 0 ? (
            <div style={emptyText}>No DataLab drafts yet. Create one above to get started.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {drafts.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className="ops-row"
                  onClick={() => navigate(`/editorial/compose/${encodeURIComponent(d.id)}`)}
                  style={draftRow}
                >
                  <KindChip kind={d.kind} />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontFamily: "var(--font-serif)",
                      fontSize: 14,
                      color: "var(--ink)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {d.title || "Untitled draft"}
                  </span>
                  <ReviewPill state={d.state} />
                  <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-4)" }}>
                    {relativeTime(d.updated_at)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

// ─── editor ──────────────────────────────────────────────────────────────────

function ComposeEditor({ id }: { id: string }) {
  const navigate = useNavigate();
  const actions = useComposeActions();
  const { detail, loading, error } = useAdminArticle(id);
  const wire = detail?.wire;

  const [title, setTitle] = useState("");
  const [dek, setDek] = useState("");
  const [kind, setKind] = useState<KindKey>("Market");
  const [bodyText, setBodyText] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [audience, setAudience] = useState<"public" | "members" | "pro">("public");
  const [leadCapture, setLeadCapture] = useState(false);
  const [slug, setSlug] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [heroImage, setHeroImage] = useState("");

  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  // uploadTarget !== null while an image upload is in flight; it also
  // records which control fired so the spinner lands in the right place.
  const [uploadTarget, setUploadTarget] = useState<"hero" | "body" | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const saveTimer = useRef<number | null>(null);
  const hydratedOnce = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);
  // Which control opened the file dialog. A ref (not state) so a
  // cancelled dialog — which fires no change event — leaves no stuck
  // spinner; uploadTarget state flips only once an upload truly starts.
  const pendingTarget = useRef<"hero" | "body" | null>(null);

  // Hydrate the form once the live article lands. A non-house article
  // can't be loaded here (the backend 404s the PATCH), so editing is
  // gated to draft / changes_requested below.
  useEffect(() => {
    if (!wire || hydratedOnce.current) return;
    setTitle(wire.title);
    setDek(wire.dek);
    setKind(wire.kind);
    setBodyText(sectionsToText(wire.body ?? []));
    setTags(wire.tags ?? []);
    setAudience(wire.audience);
    setLeadCapture(wire.lead_capture);
    setSlug(wire.slug ?? "");
    setHeroImage(wire.hero_image ?? "");
    const desc = wire.seo_meta?.description;
    setMetaDesc(typeof desc === "string" ? desc : "");
    hydratedOnce.current = true;
    setHydrated(true);
  }, [wire]);

  const editable = !wire || wire.state === "draft" || wire.state === "changes_requested";

  const buildPatch = (): PatchInput => ({
    title,
    dek,
    kind,
    hero_image: heroImage,
    body: textToSections(bodyText),
    tags,
    audience,
    lead_capture: leadCapture,
    slug: slug.trim(),
    seo_meta: metaDesc.trim() ? { description: metaDesc.trim() } : {},
  });

  async function doSave() {
    if (!actions.enabled || !editable) return;
    setSaving(true);
    try {
      await actions.save(id, buildPatch());
      setLastSavedAt(Date.now());
      setSaveError(null);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  // Debounced auto-save. Gated on `hydrated` so the empty initial form
  // never clobbers the real article on a slow fetch — same guard the
  // agent composer uses.
  useEffect(() => {
    if (!hydrated || !editable) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => void doSave(), 900);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, dek, kind, bodyText, tags, audience, leadCapture, slug, metaDesc, heroImage, hydrated, editable]);

  async function onSubmit() {
    if (!actions.enabled) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    setSubmitting(true);
    setSubmitError(null);
    try {
      await actions.save(id, buildPatch()); // flush pending edits first
      await actions.submit(id);
      navigate("/editorial/review");
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  // Image upload — pickImage opens the hidden <input>; onFilePicked
  // uploads and routes the URL to hero_image or an inline ![](url) body
  // block. The normal debounced PATCH then persists it.
  function pickImage(target: "hero" | "body") {
    if (!editable) return;
    pendingTarget.current = target;
    setUploadError(null);
    fileInput.current?.click();
  }

  async function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked later
    const target = pendingTarget.current;
    pendingTarget.current = null;
    if (!file || !target) return;
    setUploadTarget(target);
    try {
      const url = await uploadAdminArticleImage(id, file);
      if (target === "hero") {
        setHeroImage(url);
      } else {
        setBodyText((t) => (t.trim() ? t.replace(/\s*$/, "") + "\n\n" : "") + `![](${url})\n`);
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploadTarget(null);
    }
  }

  const savedLabel = saving
    ? "Saving…"
    : saveError
    ? "Save failed"
    : lastSavedAt
    ? `Saved ${Math.max(1, Math.floor((Date.now() - lastSavedAt) / 1000))}s ago`
    : hydrated
    ? "Up to date"
    : "Loading…";

  if (loading) {
    return <div style={{ padding: 40, ...emptyText }}>Loading draft…</div>;
  }
  if (error || !wire) {
    return (
      <div style={{ padding: 26 }}>
        <Notice tone="neg">Could not load this draft: {error ?? "not found"}.</Notice>
        <button type="button" style={{ ...ghostBtn, marginTop: 14 }} onClick={() => navigate("/editorial/compose")}>
          {Icon.arrowL} Back to Compose
        </button>
      </div>
    );
  }

  const canSubmit = editable && actions.enabled && !!title.trim() && !submitting;

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => void onFilePicked(e)}
        style={{ display: "none" }}
      />
      {/* toolbar */}
      <div style={toolbar}>
        <button type="button" style={ghostBtn} onClick={() => navigate("/editorial/compose")}>
          {Icon.arrowL} Compose
        </button>
        <ReviewPill state={wire.state} />
        <span style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)" }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              background: saveError ? "#b04a32" : saving ? "var(--ink-4)" : "#1d4a3a",
            }}
          />
          {savedLabel}
        </span>
        <div style={{ flex: 1 }} />
        <button type="button" style={ghostBtn} onClick={() => setPreview(true)}>
          {Icon.eye} Preview
        </button>
        <button
          type="button"
          style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.55, cursor: canSubmit ? "pointer" : "not-allowed" }}
          disabled={!canSubmit}
          onClick={() => void onSubmit()}
          title={!title.trim() ? "Add a title before submitting" : "Submit for review"}
        >
          {Icon.send} {submitting ? "Submitting…" : "Submit for review"}
        </button>
      </div>

      {(submitError || saveError || uploadError) && (
        <div style={{ padding: "12px 26px 0" }}>
          <Notice tone="neg">
            {submitError ?? uploadError ?? `Auto-save failed: ${saveError}`}
          </Notice>
        </div>
      )}
      {!editable && (
        <div style={{ padding: "12px 26px 0" }}>
          <Notice tone="warn">
            This article is <strong>{wire.state.replace(/_/g, " ")}</strong> and can no longer be
            edited from Compose. Changes are managed from the review queue.
          </Notice>
        </div>
      )}

      {/* canvas + rail */}
      <div style={{ display: "flex", flex: 1, minHeight: 0, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0, padding: "28px 32px 60px" }}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div style={{ marginBottom: 14 }}>
              <KindChip kind={kind} />
            </div>
            <textarea
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Headline that earns the click"
              rows={2}
              disabled={!editable}
              style={titleInput}
            />
            <textarea
              value={dek}
              onChange={(e) => setDek(e.target.value)}
              placeholder="One-sentence sub-headline (the dek)…"
              rows={2}
              disabled={!editable}
              style={dekInput}
            />
            <div style={bylineRow}>
              <span
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 999,
                  background: "linear-gradient(140deg,#1d4a3a,#2d6e54)",
                  color: "#f4f1ea",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                PD
              </span>
              <div>
                <div style={{ fontFamily: "var(--font-serif)", fontSize: 13, color: "var(--ink)" }}>
                  ProProp Data Lab
                </div>
                <div style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, color: "var(--ink-3)" }}>
                  House byline · ~{readMinutes(bodyText)} min read
                </div>
              </div>
            </div>
            <div style={{ display: "flex", marginTop: 18, marginBottom: -8 }}>
              <button
                type="button"
                disabled={!editable || uploadTarget !== null}
                onClick={() => pickImage("body")}
                style={smallBtn}
              >
                {Icon.plus} {uploadTarget === "body" ? "Uploading…" : "Insert image"}
              </button>
            </div>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              placeholder="Start writing…  Use ## for section headings and > for pull-quotes. Blank lines separate paragraphs."
              rows={22}
              disabled={!editable}
              style={bodyInput}
            />
          </div>
        </div>

        <aside style={rail}>
          <RailSection title="Cover image">
            {heroImage ? (
              <div>
                <img
                  src={heroImage}
                  alt="Article cover"
                  style={{ width: "100%", borderRadius: 8, display: "block", border: "1px solid var(--rule)" }}
                />
                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                  <button
                    type="button"
                    disabled={!editable || uploadTarget !== null}
                    onClick={() => pickImage("hero")}
                    style={smallBtn}
                  >
                    {uploadTarget === "hero" ? "Uploading…" : "Replace"}
                  </button>
                  <button type="button" disabled={!editable} onClick={() => setHeroImage("")} style={smallBtn}>
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                disabled={!editable || uploadTarget !== null}
                onClick={() => pickImage("hero")}
                style={{
                  ...railInput,
                  cursor: editable ? "pointer" : "default",
                  textAlign: "center",
                  color: "var(--ink-3)",
                }}
              >
                {uploadTarget === "hero" ? "Uploading…" : "Upload cover image"}
              </button>
            )}
          </RailSection>

          <RailSection title="Article kind">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {KIND_KEYS.map((k) => (
                <button
                  key={k}
                  type="button"
                  disabled={!editable}
                  onClick={() => setKind(k)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: 5,
                    cursor: editable ? "pointer" : "default",
                    fontFamily: "var(--font-ui)",
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    background: kind === k ? "var(--accent-soft)" : "transparent",
                    border: "1px solid",
                    borderColor: kind === k ? "var(--accent)" : "var(--rule)",
                    color: kind === k ? "var(--accent)" : "var(--ink-3)",
                  }}
                >
                  {k}
                </button>
              ))}
            </div>
          </RailSection>

          <RailSection title="Audience">
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {AUDIENCE_OPTS.map((opt) => {
                const on = audience === opt.value;
                return (
                  <label key={opt.value} style={{ ...radioCard, ...(on ? radioCardOn : null) }}>
                    <input
                      type="radio"
                      checked={on}
                      disabled={!editable}
                      onChange={() => setAudience(opt.value)}
                      style={{ accentColor: "var(--accent)", marginTop: 1 }}
                    />
                    <span style={{ flex: 1 }}>
                      <span style={radioLabel}>{opt.label}</span>
                      <span style={radioSub}>{opt.sub}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </RailSection>

          <RailSection title="Tags">
            <TagField value={tags} onChange={setTags} disabled={!editable} />
          </RailSection>

          <RailSection title="Lead capture">
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: editable ? "pointer" : "default" }}>
              <input
                type="checkbox"
                checked={leadCapture}
                disabled={!editable}
                onChange={(e) => setLeadCapture(e.target.checked)}
                style={{ accentColor: "var(--accent)", marginTop: 2 }}
              />
              <span>
                <span style={radioLabel}>Show lead-capture CTA</span>
                <span style={radioSub}>Adds a "start a thread" prompt at the end of the article.</span>
              </span>
            </label>
          </RailSection>

          <RailSection title="SEO &amp; sharing">
            <FieldLabel>URL slug</FieldLabel>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              disabled={!editable}
              placeholder="ura-draft-master-plan-2025"
              style={{ ...railInput, fontFamily: "var(--font-mono)" }}
            />
            <div style={{ height: 10 }} />
            <FieldLabel>Meta description</FieldLabel>
            <textarea
              value={metaDesc}
              onChange={(e) => setMetaDesc(e.target.value)}
              disabled={!editable}
              rows={3}
              placeholder="One- or two-sentence summary for search results and social cards."
              style={{ ...railInput, resize: "vertical" }}
            />
          </RailSection>
        </aside>
      </div>

      {preview && (
        <PreviewModal
          title={title}
          dek={dek}
          kind={kind}
          heroImage={heroImage}
          sections={textToSections(bodyText)}
          onClose={() => setPreview(false)}
        />
      )}
    </div>
  );
}

// ─── tag field ───────────────────────────────────────────────────────────────
//
// Typeahead bound to the admin tag catalogue. Free-text entry is
// disabled — only catalogue tags can be attached, matching the agent
// composer and the PROP-52 acceptance ("tag values exist in catalogue").

function TagField({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const { tags: catalogue } = useTags();
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const matches = useMemo(
    () =>
      catalogue
        .filter((t) => !value.includes(t.label) && !value.includes(t.tag))
        .filter((t) => !query || t.label.toLowerCase().includes(query) || t.tag.includes(query))
        .slice(0, 12),
    [catalogue, value, query],
  );

  return (
    <div>
      {value.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8 }}>
          {value.map((t) => (
            <span key={t} style={tagChip}>
              {t}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove tag ${t}`}
                  onClick={() => onChange(value.filter((x) => x !== t))}
                  style={tagChipX}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        disabled={disabled}
        placeholder="Filter tags…"
        style={railInput}
      />
      {!disabled && matches.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8, maxHeight: 150, overflow: "auto" }}>
          {matches.map((t) => (
            <button
              key={t.tag}
              type="button"
              onClick={() => {
                onChange([...value, t.label]);
                setQ("");
              }}
              style={{ ...tagChip, cursor: "pointer", border: "1px dashed var(--rule)" }}
            >
              + {t.label}
            </button>
          ))}
        </div>
      )}
      {!disabled && catalogue.length > 0 && matches.length === 0 && (
        <div style={{ ...emptyText, marginTop: 8, fontSize: 11.5 }}>
          {query ? "No matching tags — add it from Topics & tags." : "All matching tags added."}
        </div>
      )}
    </div>
  );
}

// ─── preview modal ───────────────────────────────────────────────────────────

function PreviewModal({
  title,
  dek,
  kind,
  heroImage,
  sections,
  onClose,
}: {
  title: string;
  dek: string;
  kind: KindKey;
  heroImage: string;
  sections: Section[];
  onClose: () => void;
}) {
  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <span style={pgKicker}>Preview</span>
          <div style={{ flex: 1 }} />
          <button type="button" style={ghostBtn} onClick={onClose}>
            {Icon.x} Close
          </button>
        </div>
        {heroImage && (
          <img
            src={heroImage}
            alt=""
            style={{ width: "100%", borderRadius: 10, marginBottom: 14, display: "block" }}
          />
        )}
        <div style={{ marginBottom: 12 }}>
          <KindChip kind={kind} />
        </div>
        <h1 style={{ fontFamily: "var(--font-serif)", fontSize: 30, fontWeight: 500, color: "var(--ink)", margin: "0 0 10px", letterSpacing: "-0.025em", lineHeight: 1.12 }}>
          {title || "Untitled article"}
        </h1>
        {dek && (
          <p style={{ fontFamily: "var(--font-serif)", fontSize: 16, fontStyle: "italic", color: "var(--ink-2)", margin: "0 0 8px", lineHeight: 1.5 }}>
            {dek}
          </p>
        )}
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)", marginBottom: 18 }}>
          By ProProp Data Lab
        </div>
        {sections.length === 0 ? (
          <div style={emptyText}>No body content yet.</div>
        ) : (
          sections.map((s, i) => <PreviewSection key={i} section={s} />)
        )}
      </div>
    </div>
  );
}

function PreviewSection({ section }: { section: Section }) {
  if (section.kind === "image") {
    return (
      <img
        src={section.text}
        alt=""
        style={{ width: "100%", borderRadius: 8, margin: "16px 0", display: "block" }}
      />
    );
  }
  if (section.kind === "h2") {
    return (
      <h2 style={{ fontFamily: "var(--font-serif)", fontSize: 20, fontWeight: 500, color: "var(--ink)", margin: "22px 0 8px" }}>
        {section.text}
      </h2>
    );
  }
  if (section.kind === "pull") {
    return (
      <blockquote
        style={{
          margin: "18px 0",
          padding: "4px 0 4px 16px",
          borderLeft: "3px solid var(--accent)",
          fontFamily: "var(--font-serif)",
          fontSize: 17,
          fontStyle: "italic",
          color: "var(--ink-2)",
        }}
      >
        {section.text}
      </blockquote>
    );
  }
  return (
    <p style={{ fontFamily: "var(--font-serif)", fontSize: 15.5, color: "var(--ink-2)", lineHeight: 1.65, margin: "0 0 12px" }}>
      {section.text}
    </p>
  );
}

// ─── body <-> sections round-trip ────────────────────────────────────────────
//
// The composer body is a plain textarea. `## ` lines become h2 sections,
// `> ` lines become pull-quotes, blank-line-separated blocks become
// paragraphs. callout / sources blocks (rare; not produced here) are
// rendered back to text on hydrate but collapse to paragraphs on re-edit.

function sectionsToText(sections: Section[]): string {
  return sections
    .map((s) => {
      if (s.kind === "h2") return `## ${s.text ?? ""}`;
      if (s.kind === "pull") return `> ${s.text ?? ""}`;
      if (s.kind === "image") return `![](${s.text ?? ""})`;
      if (s.kind === "callout") return [`> ${s.text ?? ""}`, ...(s.items ?? []).map((i) => `- ${i}`)].join("\n");
      if (s.kind === "sources") return `Sources: ${(s.items ?? []).join(" · ")}`;
      return s.text ?? "";
    })
    .join("\n\n");
}

function textToSections(text: string): Section[] {
  return text
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block): Section => {
      const img = block.match(/^!\[[^\]]*\]\((.+)\)$/);
      if (img) return { kind: "image", text: img[1].trim() };
      if (block.startsWith("## ")) return { kind: "h2", text: block.slice(3).trim() };
      if (block.startsWith("> ")) return { kind: "pull", text: block.slice(2).trim() };
      return { kind: "p", text: block.replace(/\s*\n\s*/g, " ").trim() };
    });
}

function readMinutes(body: string): number {
  const words = body.trim() ? body.trim().split(/\s+/).length : 0;
  return Math.max(1, Math.round(words / 220));
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const secs = (Date.now() - d.getTime()) / 1000;
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 86400 * 7) return `${Math.floor(secs / 86400)}d ago`;
  return d.toLocaleDateString("en-SG", { year: "numeric", month: "short", day: "numeric" });
}

// ─── shared bits ─────────────────────────────────────────────────────────────

function Notice({ tone, children }: { tone: "warn" | "neg"; children: React.ReactNode }) {
  const c =
    tone === "neg"
      ? { bg: "rgba(122,45,31,0.10)", fg: "#7a2d1f" }
      : { bg: "rgba(138,90,0,0.12)", fg: "#8a5a00" };
  return (
    <div
      style={{
        padding: "9px 13px",
        borderRadius: 8,
        background: c.bg,
        color: c.fg,
        fontFamily: "var(--font-ui)",
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

function RailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 9.5,
          fontWeight: 600,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "var(--ink-4)",
          marginBottom: 9,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-ui)",
        fontSize: 10,
        color: "var(--ink-4)",
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        marginBottom: 4,
      }}
    >
      {children}
    </div>
  );
}

// ─── styles ──────────────────────────────────────────────────────────────────

const panel: CSSProperties = {
  borderRadius: 10,
  border: "1px solid var(--rule)",
  background: "var(--surface)",
  padding: "16px 18px",
};
const panelTitle: CSSProperties = {
  fontFamily: "var(--font-ui)",
  fontSize: 9.5,
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "var(--ink-4)",
  marginBottom: 10,
};
const emptyText: CSSProperties = {
  fontFamily: "var(--font-serif)",
  fontStyle: "italic",
  fontSize: 13,
  color: "var(--ink-3)",
};
const draftRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 8px",
  background: "transparent",
  border: "none",
  borderRadius: 6,
  textAlign: "left",
  width: "100%",
  cursor: "pointer",
};
const toolbar: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 26px",
  borderBottom: "1px solid var(--rule)",
  background: "var(--surface)",
  position: "sticky",
  top: 0,
  zIndex: 5,
};
const rail: CSSProperties = {
  width: 312,
  flex: "none",
  alignSelf: "stretch",
  borderLeft: "1px solid var(--rule)",
  background: "var(--surface)",
  padding: "22px 20px 40px",
};
const titleInput: CSSProperties = {
  width: "100%",
  border: "none",
  outline: "none",
  background: "transparent",
  resize: "none",
  fontFamily: "var(--font-serif)",
  fontSize: 34,
  fontWeight: 500,
  color: "var(--ink)",
  letterSpacing: "-0.03em",
  lineHeight: 1.1,
  padding: 0,
};
const dekInput: CSSProperties = {
  width: "100%",
  border: "none",
  outline: "none",
  background: "transparent",
  resize: "none",
  fontFamily: "var(--font-serif)",
  fontSize: 16,
  fontStyle: "italic",
  color: "var(--ink-2)",
  lineHeight: 1.45,
  padding: 0,
  marginTop: 12,
};
const bylineRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "16px 0",
  marginTop: 14,
  borderTop: "1px solid var(--rule)",
  borderBottom: "1px solid var(--rule)",
};
const bodyInput: CSSProperties = {
  width: "100%",
  border: "none",
  outline: "none",
  background: "transparent",
  resize: "vertical",
  fontFamily: "var(--font-serif)",
  fontSize: 16,
  color: "var(--ink-2)",
  lineHeight: 1.65,
  padding: 0,
  marginTop: 24,
  minHeight: 360,
};
const railInput: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "7px 9px",
  background: "var(--bg)",
  border: "1px solid var(--rule)",
  borderRadius: 6,
  fontFamily: "var(--font-ui)",
  fontSize: 11.5,
  color: "var(--ink)",
  outline: "none",
};
const radioCard: CSSProperties = {
  display: "flex",
  gap: 8,
  padding: "8px 10px",
  borderRadius: 7,
  border: "1px solid var(--rule)",
  cursor: "pointer",
};
const radioCardOn: CSSProperties = {
  borderColor: "var(--accent)",
  background: "var(--accent-soft)",
};
const radioLabel: CSSProperties = {
  display: "block",
  fontFamily: "var(--font-ui)",
  fontSize: 12,
  fontWeight: 500,
  color: "var(--ink)",
};
const radioSub: CSSProperties = {
  display: "block",
  fontFamily: "var(--font-ui)",
  fontSize: 10.5,
  color: "var(--ink-3)",
  marginTop: 2,
};
const tagChip: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "3px 4px 3px 9px",
  borderRadius: 20,
  background: "var(--bg)",
  border: "1px solid var(--rule)",
  fontFamily: "var(--font-ui)",
  fontSize: 10.5,
  color: "var(--ink-2)",
};
const tagChipX: CSSProperties = {
  width: 14,
  height: 14,
  borderRadius: 14,
  border: "none",
  background: "transparent",
  color: "var(--ink-4)",
  cursor: "pointer",
  padding: 0,
  fontSize: 12,
  lineHeight: 1,
};
const modalBackdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(20,18,14,0.45)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "48px 20px",
  zIndex: 50,
  overflow: "auto",
};
const modalCard: CSSProperties = {
  width: "100%",
  maxWidth: 680,
  background: "var(--surface)",
  border: "1px solid var(--rule)",
  borderRadius: 12,
  padding: "20px 26px 32px",
};
